import React, { useEffect, useRef, useState } from "react";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  importCategories,
  resetCategories,
} from "@/lib/apiClient";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { DeleteCategoryDialog } from "@/components/DeleteCategoryDialog";
import { BookOpen, Plus, Pencil, Trash2, Check, X, Upload, RotateCcw, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

const CoaRow = ({ cat, onSave, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const [keywords, setKeywords] = useState(cat.keywords || "");

  const save = async () => {
    await onSave(cat.code, { name, keywords });
    setEditing(false);
  };

  return (
    <tr data-testid={`coa-row-${cat.code}`} className="align-top">
      <td className="w-28 px-5 py-2.5 font-mono text-sm font-semibold whitespace-nowrap">{cat.code}</td>
      <td className="w-[32%] px-3 py-2.5 text-sm">
        {editing ? (
          <Input data-testid={`coa-name-input-${cat.code}`} value={name} onChange={(e) => setName(e.target.value)} className="h-8 text-sm" />
        ) : (
          <span className="font-medium">{cat.name}</span>
        )}
      </td>
      <td className="px-3 py-2.5 text-sm text-muted-foreground">
        {editing ? (
          <Input data-testid={`coa-keywords-input-${cat.code}`} value={keywords} onChange={(e) => setKeywords(e.target.value)} className="h-8 text-sm" />
        ) : (
          <span className="text-xs">{cat.keywords || "—"}</span>
        )}
      </td>
      <td className="w-24 px-3 py-1.5 whitespace-nowrap text-right">
        {editing ? (
          <>
            <Button data-testid={`coa-save-btn-${cat.code}`} size="icon" variant="ghost" className="h-8 w-8 text-emerald-500" onClick={save}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(false)}>
              <X className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <>
            <Button data-testid={`coa-edit-btn-${cat.code}`} size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button data-testid={`coa-delete-btn-${cat.code}`} size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-500" onClick={() => onDelete(cat)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </td>
    </tr>
  );
};

const AddRow = ({ onAdd }) => {
  const [form, setForm] = useState({ code: "", name: "", keywords: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code and name are required");
    setBusy(true);
    try {
      if (await onAdd(form)) setForm({ code: "", name: "", keywords: "" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-2 sm:grid-cols-[110px_1fr_1.4fr_auto] items-center px-4 py-3 border-b border-border bg-muted/30">
      <Input data-testid="coa-new-code" placeholder="Code" value={form.code} onChange={set("code")} className="h-9 font-mono text-sm" />
      <Input data-testid="coa-new-name" placeholder="Account name" value={form.name} onChange={set("name")} className="h-9 text-sm" />
      <Input data-testid="coa-new-keywords" placeholder="Keywords the AI should match (comma separated)" value={form.keywords} onChange={set("keywords")} className="h-9 text-sm" />
      <Button data-testid="coa-add-btn" size="sm" onClick={submit} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add
      </Button>
    </div>
  );
};

const groupOf = (code) => {
  const c = String(code || "").trim();
  if (!/^\d+$/.test(c)) return "Other";
  if (c.length <= 3) {
    const n = parseInt(c, 10);
    if (n < 200) return "Assets, liabilities & equity";
    if (n < 300) return "Revenue";
    if (n < 400) return "Cost of sales";
    if (n < 500) return "Operating expenses";
    if (n < 600) return "Other expenses";
    return "Assets, liabilities & equity";
  }
  return { 1: "Assets, liabilities & equity", 2: "Assets, liabilities & equity", 3: "Assets, liabilities & equity", 4: "Revenue", 5: "Cost of sales", 6: "Operating expenses", 7: "Operating expenses" }[c[0]] || "Other expenses";
};
const GROUP_ORDER = ["Revenue", "Cost of sales", "Operating expenses", "Other expenses", "Assets, liabilities & equity", "Other"];

const CoaGroups = ({ cats, query, onSave, onDelete }) => {
  const q = query.trim().toLowerCase();
  const filtered = q ? cats.filter((c) => `${c.code} ${c.name} ${c.keywords || ""}`.toLowerCase().includes(q)) : cats;
  const groups = GROUP_ORDER.map((g) => [g, filtered.filter((c) => groupOf(c.code) === g)]).filter(([, l]) => l.length);
  if (!groups.length) return <p className="px-5 py-8 text-center text-sm text-muted-foreground" data-testid="coa-no-results">No GL codes match “{query}”.</p>;
  return (
    <Accordion type="multiple" defaultValue={q ? groups.map(([g]) => g) : []} key={q ? "search" : "browse"} className="divide-y divide-border">
      {groups.map(([g, list]) => (
        <AccordionItem key={g} value={g} className="border-0">
          <AccordionTrigger className="px-5 py-3 text-sm hover:no-underline hover:bg-muted/40" data-testid={`coa-group-${g.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <span className="flex items-center gap-2 font-medium">{g}<span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{list.length}</span></span>
          </AccordionTrigger>
          <AccordionContent className="pb-0">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border border-t border-border">
                {list.map((c) => <CoaRow key={c.code} cat={c} onSave={onSave} onDelete={onDelete} />)}
              </tbody>
            </table>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export const ChartOfAccountsEditor = () => {
  const { user } = useApp();
  const [cats, setCats] = useState(null);
  const [importMode, setImportMode] = useState("merge");
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const fileRef = useRef(null);

  const load = () => getCategories().then(setCats);
  useEffect(() => {
    load();
  }, []);

  const add = async (form) => {
    try {
      await createCategory(form);
      toast.success(`GL ${form.code} added`);
      await load();
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Could not add GL code"));
      return false;
    }
  };

  const save = async (code, body) => {
    try {
      await updateCategory(code, body);
      toast.success(`GL ${code} updated`);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not update GL code"));
    }
  };

  const remove = async (code, replacement) => {
    try {
      const res = await deleteCategory(code, replacement, user);
      if (res.remapped_lines > 0) {
        toast.success(`GL ${code} deleted — ${res.remapped_lines} line item(s) on ${res.remapped_invoices} invoice(s) remapped to ${res.replacement}`);
      } else {
        toast.success(`GL ${code} deleted`);
      }
      setDeleting(null);
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Could not delete GL code"));
    }
  };

  const reset = async () => {
    if (!window.confirm("Restore the default D20 chart of accounts? Your custom codes will be removed.")) return;
    const data = await resetCategories();
    setCats(data);
    toast.success("Default chart of accounts restored");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const res = await importCategories(file, importMode);
      setCats(res.categories);
      toast.success(`Imported ${res.imported} GL code(s) (${res.mode})`);
    } catch (err) {
      toast.error(errMsg(err, "CSV import failed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="coa-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-border">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <BookOpen className="h-5 w-5" />
          </span>
          <div>
            <div className="font-medium">Chart of Accounts</div>
            <div className="text-sm text-muted-foreground">
              The GL codes the AI can choose from. Keywords steer matching — edits apply to the next extraction.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={importMode} onValueChange={setImportMode}>
            <SelectTrigger data-testid="coa-import-mode" className="h-9 w-[180px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="merge">Merge into existing</SelectItem>
              <SelectItem value="replace">Replace all</SelectItem>
            </SelectContent>
          </Select>
          <input ref={fileRef} data-testid="coa-csv-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
          <Button data-testid="coa-import-btn" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}>
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
            Import CSV
          </Button>
          <Button data-testid="coa-reset-btn" variant="ghost" size="sm" onClick={reset}>
            <RotateCcw className="h-4 w-4 mr-1.5" /> Defaults
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-border bg-muted/20">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="coa-search" placeholder="Search code, account or keyword…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-9 pl-8 text-sm" />
        </div>
        <Button data-testid="coa-add-toggle" size="sm" variant={adding ? "secondary" : "default"} onClick={() => setAdding((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" /> {adding ? "Close" : "Add GL code"}
        </Button>
      </div>
      {adding && <AddRow onAdd={async (f) => { const ok = await add(f); if (ok) setAdding(false); return ok; }} />}
      {!cats ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div>
          <CoaGroups cats={cats} query={query} onSave={save} onDelete={setDeleting} />
          <div className="px-4 py-2.5 text-xs text-muted-foreground border-t border-border" data-testid="coa-count">
            {cats.length} GL codes in {GROUP_ORDER.filter((g) => cats.some((c) => groupOf(c.code) === g)).length} groups · click a group to expand · CSV format: <span className="font-mono">code,name,keywords</span>
          </div>
        </div>
      )}
      <DeleteCategoryDialog cat={deleting} categories={cats || []} onConfirm={remove} onClose={() => setDeleting(null)} />
    </div>
  );
};
